/**
 * End of match: winner, the match story, standings, the numbers, and what next — MENU plus,
 * when there is one, a way to play again (offline REMATCH, QUICK PLAY, BACK TO ROOM). The
 * buttons can change while the screen is up (`setResultActions`: the room reopens a moment later).
 *
 * The story (the `end` message's chronicle, state/story.ts): a timeline from the full clock to
 * 0:00 in three lanes (DIPLO · WAR · LEAD) with WEAPONS RELEASE and CAPITALS EXPOSED marked, one
 * mark per moment in its nation's colour (pointing at / clicking one tells it on the line
 * below), and the key moments in words beside the standings. Without a chronicle (an old server)
 * the screen is standings and numbers only.
 */
import type { ChronicleKind, EndMessage } from '@sa/protocol';
import { BALANCE, type PlayerId, type PlayerInfo } from '@sa/sim';
import { Palette, drawPatternSwatch } from '../../render/palette';
import { leftAt } from '../../state/alerts';
import { LANES, axisTicks, clockAt, keyMoments, numberRows, phaseMarks, storyMarks, type StoryMark } from '../../state/story';
import { cssRgb } from '../../theme';
import { h } from '../dom';

const REASON: Record<EndMessage['reason'], string> = {
  elimination: 'LAST NATION STANDING',
  time: 'TIME UP · HIGHEST SCORE',
  domination: 'DOMINATION',
  surrender: 'SURRENDER',
};

export interface ResultAction {
  label: string;
  run(): void;
  primary?: boolean;
}

/** Replace the results screen's buttons. */
export function setResultActions(screen: HTMLElement, actions: readonly ResultAction[]): void {
  screen
    .querySelector('.res-actions')
    ?.replaceChildren(...actions.map((a) => h(`button.btn${a.primary ? '.primary' : ''}`, { onclick: () => a.run() }, a.label)));
}

/** 12×12 line icons per kind of moment, drawn in the nation's colour (stroke / fill = currentColor). */
const ICON: Record<ChronicleKind, string> = {
  ally: '<circle cx="4.2" cy="6" r="2.7"/><circle cx="7.8" cy="6" r="2.7"/>',
  betrayal: '<path d="M6 .8 7.3 3v4.6H4.7V3Z" fill="currentColor" stroke="none"/><path d="M2.8 8.2h6.4M6 8.2v3"/>',
  ceasefire: '<path d="M4 2.5v7M8 2.5v7"/>',
  truceBroken: '<path d="M4 2.5v7M8 2.5v7M1.5 10.5l9-9"/>',
  gift: '<path d="M6 1 11 6 6 11 1 6Z"/><path d="M6 4v4M4 6h4"/>',
  firstLaunch: '<path d="M6 .8 9 4.6H7.2V11H4.8V4.6H3Z"/>',
  strike: '<circle cx="6" cy="6" r="2" fill="currentColor"/><path d="M6 .5v2M6 9.5v2M.5 6h2M9.5 6h2M2.1 2.1l1.4 1.4M8.5 8.5l1.4 1.4M2.1 9.9l1.4-1.4M8.5 3.5l1.4-1.4"/>',
  critical: '<path d="M6 1 11.2 10.5H.8Z"/><path d="M6 4.4v3M6 8.6v.6"/>',
  recovered: '<path d="M2 6.4 4.8 9.2 10 3"/>',
  out: '<path d="M1.5 1.5h9v9h-9Z"/><path d="M3.6 3.6l4.8 4.8M8.4 3.6 3.6 8.4"/>',
  leader: '<path d="M1.5 9.5v-6L4 6l2-3.5L8 6l2.5-2.5v6Z"/>',
  domination: '<path d="M2 2h8v8H2Z" fill="currentColor" fill-opacity=".35"/>',
  final: '<path d="M2.5 1.5h7l-7 9h7Z"/>',
  end: '<path d="M2.5 11.2V1.2h7L8 3.7l1.5 2.5h-7"/>',
};

function icon(kind: ChronicleKind): HTMLElement {
  const s = h('span.ico');
  s.innerHTML = `<svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" stroke-linecap="round">${ICON[kind]}</svg>`;
  return s;
}

/** `matchTicks`: the match length — moments are told as the clock showed them (`12:08`, `OUT 9:54 LEFT`). */
export function resultsScreen(end: EndMessage, me: PlayerId, players: PlayerInfo[], actions: readonly ResultAction[], matchTicks: number = BALANCE.matchTicks): HTMLElement {
  const name = (id: PlayerId) => (players.find((p) => p.id === id)?.name ?? `P${id}`).toUpperCase();
  const won = end.winners.includes(me);
  const winners = end.winners.map(name).join(' + ') || 'NOBODY';
  const standings = [...end.standings].sort((a, b) => b.score - a.score);
  // the same territory swatch as in the match (Tab), so a nation reads the same everywhere
  const pal = new Palette(me, players);
  const colour = (id: PlayerId) => cssRgb(pal.color(id));
  const swatch = (id: PlayerId) => {
    const c = h('canvas.sw', { width: 10, height: 10 });
    drawPatternSwatch(c.getContext('2d')!, pal.pattern(id), pal.color(id));
    return c;
  };
  const pct = (x: number | undefined) => (x === undefined ? '—' : `${Math.round(x * 100)}%`);

  const table = h(
    'div.standings',
    {},
    h('div.st.cap', {}, h('span', {}, 'SHARE OF ALL')),
    h(
      'div.st.head',
      {},
      h('span', {}, '#'),
      h('span', {}, 'NATION'),
      h('span', {}, 'SCORE'),
      h('span', { title: 'share of all land' }, 'LAND'),
      h('span', { title: 'share of all population' }, 'POP'),
      h('span', { title: 'share of all the population killed on rival land (a share, not a hit rate)' }, 'KILLS'),
    ),
    ...standings.map((s, i) =>
      h(
        'div.st',
        { style: { color: s.playerId === me ? 'var(--me)' : 'var(--white)' } },
        h('span', {}, String(i + 1)),
        h('span', {}, swatch(s.playerId), name(s.playerId), s.eliminatedTick !== undefined ? h('span.out', {}, ` · OUT ${leftAt(s.eliminatedTick, matchTicks)}`) : null),
        h('span', {}, String(s.score)),
        h('span', {}, pct(s.areaShare)),
        h('span', {}, pct(s.popShare)),
        h('span', {}, pct(s.killShare)),
      ),
    ),
  );

  // the numbers: ours as a two-column list; with other humans in the match, a column each (us first)
  const humans = [me, ...players.filter((p) => !p.isBot && p.id !== me).map((p) => p.id)].filter((id) => end.stats.players.some((p) => p.id === id));
  const rows = numberRows(end, humans);
  const numbers = !humans.length
    ? null
    : humans.length === 1
      ? h('div.res-nums', {}, h('div.res-h', {}, 'YOUR WAR'), h('div.nv-list', {}, ...rows.map((r) => h('div.nv', { title: r.title }, h('span', {}, r.label), h('b', {}, r.values[0])))))
      : h(
          'div.res-nums',
          {},
          h('div.res-h', {}, 'PLAYERS'),
          h(
            'div.nums',
            { style: { gridTemplateColumns: `minmax(0, 1fr) repeat(${humans.length}, minmax(52px, auto))` } },
            h('span'),
            ...humans.map((id) => h('span.nh', { style: { color: id === me ? 'var(--me)' : 'var(--white)' } }, swatch(id), id === me ? 'YOU' : name(id))),
            ...rows.flatMap((r) => [h('span.lbl', { title: r.title }, r.label), ...r.values.map((v) => h('span', {}, v))]),
          ),
        );

  const marks = end.chronicle ? storyMarks(end.chronicle, name, matchTicks) : [];
  const story = marks.length ? storyBlock(marks, end, me, matchTicks, colour, swatch) : null;

  const el = h(
    'div.screen.results',
    {},
    h(
      `div.col.res-panel${story ? '.told' : ''}`,
      {},
      h(
        'div.winner',
        { style: { color: won ? 'var(--me)' : 'var(--amber)', textShadow: `0 0 16px ${won ? 'rgba(96,255,160,.6)' : 'rgba(255,172,52,.5)'}` } },
        won ? 'VICTORY' : 'DEFEAT',
      ),
      h('div.sub', {}, `${winners} · ${REASON[end.reason]}`),
      story?.timeline ?? null,
      h('div.res-body', {}, h('div.res-main', {}, table), story || numbers ? h('div.res-side', {}, story?.moments ?? null, numbers) : null),
      h('div.row-inline.res-actions'),
    ),
  );
  setResultActions(el, actions);
  return el;
}

/** The timeline (with its detail line) and the key-moments list, wired to each other. */
function storyBlock(
  marks: StoryMark[],
  end: EndMessage,
  me: PlayerId,
  matchTicks: number,
  colour: (id: PlayerId) => string,
  swatch: (id: PlayerId) => HTMLCanvasElement,
): { timeline: HTMLElement; moments: HTMLElement } {
  // two rows of 18 px marks per lane, inside it (never into the lane above or the phase labels)
  const LANE_H = 40;
  const ROW_Y = [10, 29];
  const keys = keyMoments(marks, 7, me);
  const isKey = new Set(keys);
  const detail = h('div.tl-detail');
  const idle = () => detail.replaceChildren(h('span.dim', {}, `${marks.length} MOMENTS · POINT AT A MARK`));
  let pinned: StoryMark | null = null;
  const btns = new Map<StoryMark, HTMLElement>();
  const rowsOf = new Map<StoryMark, HTMLElement>();

  const tell = (m: StoryMark | null) => {
    for (const [k, b] of btns) b.classList.toggle('hot', k === m || k === pinned);
    for (const [k, r] of rowsOf) r.classList.toggle('hot', k === m || k === pinned);
    const show = m ?? pinned;
    if (!show) return idle();
    detail.replaceChildren(h('span.at', {}, show.at), swatch(show.e.a), h('span', { style: { color: show.e.a === me ? 'var(--me)' : 'var(--white)' } }, show.text));
  };
  const pin = (m: StoryMark) => {
    pinned = pinned === m ? null : m;
    tell(m);
  };

  const plot = h('div.tl-plot', { style: { height: `${LANES.length * LANE_H}px` } });
  for (let l = 0; l < LANES.length; l++) plot.append(h('i.tl-lane', { style: { top: `${l * LANE_H + ROW_Y[1]}px` } }));
  const endX = Math.min(1, (end.stats.durationTicks || matchTicks) / matchTicks);
  if (endX < 0.999) plot.append(h('div.tl-after', { style: { left: `${(endX * 100).toFixed(2)}%` } }));
  const phases = phaseMarks(matchTicks);
  for (const p of phases) plot.append(h('i.tl-phase', { style: { left: `${(p.x * 100).toFixed(2)}%` } }));
  for (const m of marks) {
    const b = h(
      'button.tl-m',
      {
        type: 'button',
        style: { left: `${(m.x * 100).toFixed(2)}%`, top: `${m.lane * LANE_H + ROW_Y[m.row ? 0 : 1]}px`, color: colour(m.e.a) },
        'aria-label': `${m.at} ${m.text}`,
        onmouseenter: () => tell(m),
        onmouseleave: () => tell(null),
        onfocus: () => tell(m),
        onblur: () => tell(null),
        onclick: () => pin(m),
      },
      icon(m.e.k),
    );
    if (isKey.has(m)) b.classList.add('key');
    btns.set(m, b);
    plot.append(b);
  }

  const top = h('div.tl-top', {}, ...phases.map((p) => h('span', { style: { left: `${(p.x * 100).toFixed(2)}%` } }, p.label, h('em', {}, ` ${p.at}`))));
  // (not on top of a phase label: the hatch says it as well)
  if (endX < 0.999 && phases.every((p) => Math.abs(p.x - endX) > 0.12)) top.append(h('span.end', { style: { left: `${(endX * 100).toFixed(2)}%` } }, `END ${clockAt(end.stats.durationTicks, matchTicks)}`));
  const axis = h('div.tl-axis', {}, ...axisTicks(matchTicks).map((t) => h('span', { style: { left: `${(t.x * 100).toFixed(2)}%` } }, t.label)));
  const timeline = h(
    'div.tl',
    {},
    h('div.res-h', {}, 'MATCH STORY'),
    h(
      'div.tl-grid',
      {},
      h('div.tl-names', {}, ...LANES.map((l) => h('span', { style: { height: `${LANE_H}px` } }, l))),
      h('div.tl-body', {}, top, plot, axis),
    ),
    detail,
  );
  idle();

  const moments = h(
    'div.res-moments',
    {},
    h('div.res-h', {}, 'KEY MOMENTS'),
    ...keys.map((m) => {
      const r = h(
        `div.km${m.e.a === me || m.e.b === me ? '.mine' : ''}`,
        {
          onmouseenter: () => tell(m),
          onmouseleave: () => tell(null),
          onclick: () => pin(m),
        },
        h('span.at', {}, m.at),
        h('span.ico-c', { style: { color: colour(m.e.a) } }, icon(m.e.k)),
        h('span.txt', {}, m.text),
      );
      rowsOf.set(m, r);
      return r;
    }),
  );
  return { timeline, moments };
}
